import { ApiError } from '@/lib/api'

/**
 * The capture queue: what one photographed page can be, and how an upload
 * failure is read.
 *
 * It is kept out of the component because every one of these decisions is a
 * rule rather than a rendering — the size check, which items a batch send
 * picks up, when a failure stops the batch instead of marking one item — and
 * each of them is otherwise only reachable by standing in front of a filing
 * cabinet with a phone.
 */

export type CaptureItemStatus = 'ready' | 'sending' | 'sent' | 'error' | 'tooLarge'

export interface CaptureItem {
  readonly id: string
  readonly file: File
  /** When the photo was taken, i.e. when it entered the queue. */
  readonly takenAt: Date
  /** Object URL of the image, for the thumbnail; revoked when the item goes. */
  readonly previewUrl?: string
  readonly status: CaptureItemStatus
  /** 0..1, from the upload progress event. */
  readonly progress: number
  readonly error?: string
}

/**
 * The upload only ever accepts images, even though `GET /public/upload/:token`
 * advertises `application/pdf` as well: the public POST stores a PDF and
 * answers `needsConversion: true`, but nothing on that path enqueues the
 * conversion, so the pages are never rendered and the document is stranded.
 * Offering a file type that silently goes nowhere is worse than not offering
 * it — and this is a camera anyway.
 */
export const CAPTURE_ACCEPT = 'image/jpeg,image/png,image/*'

let sequence = 0

/** A fresh queue item, already carrying the verdict of the size check. */
export function newCaptureItem(
  file: File,
  maxSizeMb: number,
  options: { id?: string; now?: Date; previewUrl?: string } = {},
): CaptureItem {
  const maxBytes = maxSizeMb > 0 ? maxSizeMb * 1024 * 1024 : 0
  return {
    id: options.id ?? `capture-${Date.now()}-${(sequence += 1)}`,
    file,
    takenAt: options.now ?? new Date(),
    previewUrl: options.previewUrl,
    // Checked here rather than at send time so a photo that can never arrive
    // says so while the person is still standing over the page, not after
    // twenty more have been shot.
    status: maxBytes > 0 && file.size > maxBytes ? 'tooLarge' : 'ready',
    progress: 0,
  }
}

/** Items a "senden" press picks up: never a sent one, never an oversized one. */
export function isSendable(item: CaptureItem): boolean {
  return item.status === 'ready' || item.status === 'error'
}

export function sendableItems(items: readonly CaptureItem[]): CaptureItem[] {
  return items.filter(isSendable)
}

export function sendButtonLabel(count: number): string {
  return count === 1 ? '1 Formular senden' : `${count} Formulare senden`
}

/** True once everything in a non-empty queue has arrived. */
export function allSent(items: readonly CaptureItem[]): boolean {
  return items.length > 0 && items.every((item) => item.status === 'sent')
}

export function tooLargeNote(maxSizeMb: number): string {
  return `zu groß (max. ${maxSizeMb} MB)`
}

/** "Seite 3 · 14:02" — the index is the queue position, not a server page. */
export function itemLabel(index: number, takenAt: Date): string {
  const time = takenAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  return `Seite ${index + 1} · ${time}`
}

/**
 * How an upload failure is to be treated.
 *
 * - `item` — this photo failed, the rest of the batch can go on, and the row
 *   offers "Erneut senden".
 * - `rateLimit` — the token's hourly cap (60 uploads, Redis INCR + EXPIRE) is
 *   spent. Every further request would be refused too, so the batch stops and
 *   the item goes back to `ready` rather than looking broken: nothing is wrong
 *   with the photo.
 * - `linkGone` — the link was revoked or expired mid-session. Nothing more can
 *   be sent through it at all, so the whole screen changes.
 */
export interface UploadFailure {
  kind: 'item' | 'rateLimit' | 'linkGone'
  message: string
}

/**
 * The gateway sends no `Retry-After` and the counter is a plain one-hour key
 * started by the first upload, so the honest statement is "in about an hour",
 * not a clock time we would be inventing.
 */
export const RATE_LIMIT_MESSAGE =
  'Über diesen Link sind 60 Aufnahmen pro Stunde möglich, und dieses Kontingent ist aufgebraucht. ' +
  'Die übrigen Aufnahmen bleiben in der Liste und lassen sich in etwa einer Stunde senden.'

export const LINK_GONE_MESSAGE = 'Dieser Link ist nicht mehr gültig. Bitte einen neuen anfordern.'

export function classifyUploadError(error: unknown): UploadFailure {
  const api = ApiError.from(error)
  if (api.status === 429) return { kind: 'rateLimit', message: RATE_LIMIT_MESSAGE }
  if (api.isNotFound) return { kind: 'linkGone', message: LINK_GONE_MESSAGE }
  if (api.code === 'network_error') {
    // The artboard's wording for an item that could not reach the server.
    return { kind: 'item', message: 'Keine Verbindung' }
  }
  if (api.status === 503) {
    // The gateway's upload semaphore is full. Sequential sending rarely hits
    // it, but another phone on the same instance can — and it clears on its
    // own, so this is a retry, not a defect.
    return { kind: 'item', message: 'Server gerade ausgelastet' }
  }
  if (api.status === 413) return { kind: 'item', message: 'Aufnahme zu groß für den Server' }
  return { kind: 'item', message: api.message }
}
